import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppDispatch, RootState } from '../store';
import { initiateColorPayment } from '../store/colorsSlice';
import { toastError } from '../utils/toast';
import { trackEvent } from '../utils/analytics';

interface ColorPickerModalProps {
  open: boolean;
  title: string;
  currentColor: string;
  onClose: () => void;
  onApply: (color: string) => void;
}

export const ColorPickerModal: React.FC<ColorPickerModalProps> = ({
  open,
  title,
  currentColor,
  onClose,
  onApply,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  const catalog      = useSelector((s: RootState) => s.colors.catalog);
  const hasSub       = useSelector((s: RootState) => s.colors.subscription.active);

  // Colors shown in the grid: free + individually purchased
  const displayColors = catalog.length > 0
    ? catalog.filter(c => c.tier === 'free' || c.purchased).map(c => c.color)
    : ['#000000','#FFFFFF','#FF0000','#00FF00','#0000FF','#FFFF00',
       '#FF00FF','#00FFFF','#888888','#FF8800','#8800FF','#FF0088',
       '#00FF88','#0088FF','#FFCC00','#444444'];

  const [selected,      setSelected]     = useState(currentColor.toUpperCase());
  const [hexInput,      setHexInput]      = useState(currentColor.replace(/^#/, '').toUpperCase());
  const [hexValid,      setHexValid]      = useState(true);
  const [buyPanelOpen,  setBuyPanelOpen]  = useState(false);
  const [buyHex,        setBuyHex]        = useState('');
  const [buyHexValid,   setBuyHexValid]   = useState(false);
  const [buyHexOwned,   setBuyHexOwned]   = useState(false);
  const [buyingColor,   setBuyingColor]   = useState(false);
  const [keyHint,       setKeyHint]       = useState('');

  const keyBufferRef      = useRef('');
  const keyTimerRef       = useRef<ReturnType<typeof setTimeout> | null>(null);
  const displayColorsRef  = useRef(displayColors);
  displayColorsRef.current = displayColors;

  // handleApply captured via ref so keydown handler always sees latest state
  const handleApplyRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (!open) return;

    const commit = () => {
      const buf = keyBufferRef.current;
      keyBufferRef.current = '';
      setKeyHint('');
      const idx = parseInt(buf, 10) - 1;
      const colors = displayColorsRef.current;
      if (idx >= 0 && idx < colors.length) {
        const c = colors[idx];
        setSelected(c);
        setHexInput(c.replace(/^#/, ''));
        setHexValid(true);
        setBuyPanelOpen(false);
      }
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }

      const isInputFocused =
        document.activeElement instanceof HTMLInputElement ||
        document.activeElement instanceof HTMLTextAreaElement;

      if (e.key === 'Enter' && !isInputFocused) {
        e.preventDefault();
        // flush pending digit selection first
        if (keyTimerRef.current) {
          clearTimeout(keyTimerRef.current);
          keyTimerRef.current = null;
          commit();
        }
        handleApplyRef.current();
        return;
      }

      if (/^\d$/.test(e.key) && !isInputFocused) {
        e.preventDefault();
        keyBufferRef.current += e.key;
        setKeyHint(keyBufferRef.current);

        if (keyTimerRef.current) clearTimeout(keyTimerRef.current);
        keyTimerRef.current = setTimeout(() => {
          keyTimerRef.current = null;
          commit();
        }, 600);
      }
    };

    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      if (keyTimerRef.current) { clearTimeout(keyTimerRef.current); keyTimerRef.current = null; }
      keyBufferRef.current = '';
      setKeyHint('');
    };
  }, [open, onClose]);

  // Sync state when modal opens with a new currentColor
  useEffect(() => {
    if (open) {
      const c = currentColor.toUpperCase();
      setSelected(c);
      setHexInput(c.replace(/^#/, ''));
      setHexValid(true);
      setBuyPanelOpen(false);
      setBuyHex('');
      setBuyHexValid(false);
    }
  }, [open, currentColor]);

  if (!open) return null;

  // ── Grid swatch click ──────────────────────────────────────────
  const selectColor = (c: string) => {
    setSelected(c);
    setHexInput(c.replace(/^#/, ''));
    setHexValid(true);
    setBuyPanelOpen(false);
  };

  // ── Hex input (subscription mode) ─────────────────────────────
  const handleHexInput = (val: string) => {
    const clean = val.replace(/^#/, '').toUpperCase().slice(0, 6);
    setHexInput(clean);
    const full = '#' + clean;
    if (/^#[0-9A-Fa-f]{6}$/.test(full)) {
      setSelected(full);
      setHexValid(true);
    } else {
      setHexValid(false);
    }
  };

  // ── Buy-panel hex ──────────────────────────────────────────────
  const handleBuyHexInput = (val: string) => {
    const clean = val.replace(/^#/, '').toUpperCase().slice(0, 6);
    setBuyHex(clean);
    const isValidHex = /^[0-9A-Fa-f]{6}$/.test(clean);
    setBuyHexValid(isValidHex);
    if (isValidHex) {
      const full = '#' + clean;
      // Owned if: subscription active (any hex), or catalog entry exists and is owned
      const owned = hasSub || (catalog.find(c => c.color.toUpperCase() === full)?.owned ?? false);
      setBuyHexOwned(owned);
    } else {
      setBuyHexOwned(false);
    }
  };

  const handleBuyNewColor = async () => {
    if (!buyHexValid) return;
    const color = '#' + buyHex;
    setBuyingColor(true);
    trackEvent('color_purchase_initiate', { color, from: 'color_picker' });
    try {
      const returnPath = location.pathname;
      const paymentUrl = await dispatch(initiateColorPayment({ color, returnPath })).unwrap();
      window.location.href = paymentUrl;
    } catch (e: any) {
      toastError(e?.message || t('shop.errBuy'));
      setBuyingColor(false);
    }
    // Don't reset buyingColor on success — page is navigating away
  };

  const handleApply = () => {
    if (/^#[0-9A-Fa-f]{6}$/i.test(selected)) {
      onApply(selected);
    }
    onClose();
  };
  handleApplyRef.current = handleApply;

  return ReactDOM.createPortal(
    <div className="pixel-modal-overlay" onClick={onClose}>
      <div className="pixel-modal pixel-modal--color" onClick={e => e.stopPropagation()}>
        <h3 className="pixel-modal__title">{title}</h3>

        {/* Preview + keyboard hint */}
        <div className="color-picker__preview-row">
          <div className="color-picker__preview" style={{ backgroundColor: selected }} />
          <span className={`color-picker__key-hint${keyHint ? ' color-picker__key-hint--active' : ''}`}>{keyHint.padStart(3, '0')}</span>
        </div>
        <div className="color-picker__grid">
          {displayColors.map((c, i) => (
            <button
              key={c}
              type="button"
              className={[
                'color-picker__swatch',
                selected === c ? 'color-picker__swatch--active' : '',
              ].join(' ').trim()}
              style={{ backgroundColor: c }}
              onClick={() => selectColor(c)}
              title={`${i + 1}: ${c}`}
            >
              <span className="color-picker__swatch-num">{i + 1}</span>
            </button>
          ))}

          {/* "+" button — only for non-subscribers */}
          {!hasSub && (
            <button
              type="button"
              className="color-picker__swatch color-picker__swatch--add"
              onClick={() => setBuyPanelOpen(v => !v)}
              title={t('shop.buyColorBtn')}
            >
              +
            </button>
          )}
        </div>

        {/* Buy-panel: shown when "+" clicked (non-subscriber) */}
        {!hasSub && buyPanelOpen && (
          <div className="color-picker__buy-panel">
            <div className="color-picker__buy-preview"
              style={{ backgroundColor: buyHexValid ? '#' + buyHex : '#222' }} />
            <div className="color-picker__hex-row">
              <span className="color-picker__hex-label">#</span>
              <input
                className="pixel-modal__input color-picker__hex-input"
                type="text"
                value={buyHex}
                onChange={e => handleBuyHexInput(e.target.value)}
                maxLength={6}
                placeholder="FF4400"
                spellCheck={false}
                autoFocus
              />
            </div>
            <div className="color-picker__buy-actions">
              <button
                type="button"
                className="shop-color-item__buy-btn color-picker__buy-confirm"
                onClick={handleBuyNewColor}
                disabled={!buyHexValid || buyHexOwned || buyingColor}
                title={buyHexOwned ? t('shop.colorAlreadyOwned') : undefined}
              >
                {buyingColor ? '...' : buyHexOwned ? t('shop.colorAlreadyOwned') : t('shop.buyColorBtn')}
              </button>
              <span
                className="color-picker__locked-link"
                onClick={() => { onClose(); navigate('/shop'); }}
              >
                {t('shop.goToShop')}
              </span>
            </div>
            <p style={{ fontSize: '10px', opacity: 0.55, marginTop: '6px', textAlign: 'center' }}>
              {t('common.ofertaConsent')}{' '}
              <Link to="/about?tab=oferta" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--px-accent)', textDecoration: 'underline' }}>
                {t('common.ofertaLink')}
              </Link>
            </p>
          </div>
        )}

        {/* Hex input: only for subscribers */}
        {hasSub && (
          <div className="color-picker__hex-row">
            <span className="color-picker__hex-label">#</span>
            <input
              className={[
                'pixel-modal__input color-picker__hex-input',
                !hexValid ? 'color-picker__hex-input--invalid' : '',
              ].join(' ').trim()}
              type="text"
              value={hexInput}
              onChange={e => handleHexInput(e.target.value)}
              maxLength={6}
              placeholder="FFFFFF"
              spellCheck={false}
            />
          </div>
        )}

        {/* Actions */}
        <div className="pixel-modal__actions">
          <button type="button" className="pixel-modal__btn" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="pixel-modal__btn pixel-modal__btn--primary"
            onClick={handleApply}
            disabled={!hexValid}
          >
            {t('common.apply')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
